// Extend the generated OpenAPI clients with cache tags + friendly hook names.
// Five generated modules back this feature: scheduling-gates, availability,
// scheduling-confirmation, booking-prospects, and scheduling-change-requests.
// All inject into the same baseApi, so enhancing each is additive.
import { enhancedApi as availabilityGeneratedApi } from '@store/generated/availability';
import { enhancedApi as prospectsGeneratedApi } from '@store/generated/booking-prospects';
import { enhancedApi as changeRequestsGeneratedApi } from '@store/generated/scheduling-change-requests';
import { enhancedApi as confirmationGeneratedApi } from '@store/generated/scheduling-confirmation';
import { enhancedApi as gatesGeneratedApi } from '@store/generated/scheduling-gates';

const GATE_TAGS = ['SchedulingGate', 'SchedulingGateList'] as const;

export const schedulingGatesApi = gatesGeneratedApi.enhanceEndpoints({
  addTagTypes: GATE_TAGS,
  endpoints: {
    getV2ProviderSchedulingGates: {
      providesTags: result =>
        result
          ? [
              ...result.items.map(g => ({
                type: 'SchedulingGate' as const,
                id: g.policy_id,
              })),
              { type: 'SchedulingGateList' as const, id: 'LIST' },
            ]
          : [{ type: 'SchedulingGateList' as const, id: 'LIST' }],
    },
    postV2ProviderSchedulingGates: {
      invalidatesTags: [{ type: 'SchedulingGateList' as const, id: 'LIST' }],
    },
    patchV2ProviderSchedulingGatesByPolicyId: {
      invalidatesTags: (_result, _error, { policyId }) => [
        { type: 'SchedulingGate' as const, id: policyId },
        { type: 'SchedulingGateList' as const, id: 'LIST' },
      ],
    },
    deleteV2ProviderSchedulingGatesByPolicyId: {
      invalidatesTags: (_result, _error, { policyId }) => [
        { type: 'SchedulingGate' as const, id: policyId },
        { type: 'SchedulingGateList' as const, id: 'LIST' },
      ],
    },
  },
});

const AVAILABILITY_TAGS = ['AvailabilityRule', 'AvailabilityRuleList'] as const;

export const availabilityRulesApi = availabilityGeneratedApi.enhanceEndpoints({
  addTagTypes: AVAILABILITY_TAGS,
  endpoints: {
    getV2ProviderAvailabilityRules: {
      providesTags: result =>
        result?.items
          ? [
              ...result.items.map(r => ({
                type: 'AvailabilityRule' as const,
                id: r.availability_rule_id ?? 'unknown',
              })),
              { type: 'AvailabilityRuleList' as const, id: 'LIST' },
            ]
          : [{ type: 'AvailabilityRuleList' as const, id: 'LIST' }],
    },
    postV2ProviderAvailabilityRules: {
      invalidatesTags: [{ type: 'AvailabilityRuleList' as const, id: 'LIST' }],
    },
    patchV2ProviderAvailabilityRulesByAvailabilityRuleId: {
      invalidatesTags: (_result, _error, { availabilityRuleId }) => [
        { type: 'AvailabilityRule' as const, id: availabilityRuleId },
        { type: 'AvailabilityRuleList' as const, id: 'LIST' },
      ],
    },
    deleteV2ProviderAvailabilityRulesByAvailabilityRuleId: {
      invalidatesTags: (_result, _error, { availabilityRuleId }) => [
        { type: 'AvailabilityRule' as const, id: availabilityRuleId },
        { type: 'AvailabilityRuleList' as const, id: 'LIST' },
      ],
    },
  },
});

// 'Appointment' is the appointments feature's tag type; declaring it here too
// is additive on the shared baseApi and lets booking actions refresh the
// calendar + detail-drawer caches.
const CONFIRMATION_TAGS = [
  'SchedulingConfirmation',
  'Appointment',
  'AppointmentsList',
  'BookingProspectList',
] as const;

export const schedulingConfirmationApi = confirmationGeneratedApi.enhanceEndpoints({
  addTagTypes: CONFIRMATION_TAGS,
  endpoints: {
    getV2ProviderSchedulingConfirmation: {
      providesTags: [{ type: 'SchedulingConfirmation' as const, id: 'LIST' }],
    },
    putV2ProviderSchedulingConfirmation: {
      invalidatesTags: [{ type: 'SchedulingConfirmation' as const, id: 'LIST' }],
    },
    deleteV2ProviderSchedulingConfirmationByPolicyId: {
      invalidatesTags: [{ type: 'SchedulingConfirmation' as const, id: 'LIST' }],
    },
    postV2ProviderSchedulingBookingsByAppointmentIdConfirm: {
      // The queue rows surface appointment_status, so the prospect list
      // refreshes too.
      invalidatesTags: (_result, _error, { appointmentId }) => [
        { type: 'Appointment' as const, id: appointmentId },
        { type: 'AppointmentsList' as const, id: 'LIST' },
        { type: 'BookingProspectList' as const, id: 'LIST' },
      ],
    },
  },
});

const PROSPECT_TAGS = [
  'BookingProspect',
  'BookingProspectList',
  'Appointment',
  'AppointmentsList',
] as const;

export const bookingProspectsApi = prospectsGeneratedApi.enhanceEndpoints({
  addTagTypes: PROSPECT_TAGS,
  endpoints: {
    getV2ProviderSchedulingProspects: {
      providesTags: result =>
        result
          ? [
              ...result.items.map(p => ({
                type: 'BookingProspect' as const,
                id: p.booking_prospect_id,
              })),
              { type: 'BookingProspectList' as const, id: 'LIST' },
            ]
          : [{ type: 'BookingProspectList' as const, id: 'LIST' }],
    },
    getV2ProviderSchedulingProspectsByBookingProspectId: {
      providesTags: (_result, _error, { bookingProspectId }) => [
        { type: 'BookingProspect' as const, id: bookingProspectId },
      ],
    },
    getV2ProviderSchedulingProspectsByBookingProspectIdMatches: {
      providesTags: (_result, _error, { bookingProspectId }) => [
        { type: 'BookingProspect' as const, id: bookingProspectId },
      ],
    },
    // Promote re-points the appointment to a patient; decline cancels it.
    // Invalidate appointment caches so the calendar + detail drawer reflect
    // the change without a manual refetch.
    postV2ProviderSchedulingProspectsByBookingProspectIdPromote: {
      invalidatesTags: (result, _error, { bookingProspectId }) => [
        { type: 'BookingProspect' as const, id: bookingProspectId },
        { type: 'BookingProspectList' as const, id: 'LIST' },
        { type: 'AppointmentsList' as const, id: 'LIST' },
        ...(result?.prospect.appointment_id
          ? [{ type: 'Appointment' as const, id: result.prospect.appointment_id }]
          : []),
      ],
    },
    deleteV2ProviderSchedulingProspectsByBookingProspectId: {
      invalidatesTags: (result, _error, { bookingProspectId }) => [
        { type: 'BookingProspectList' as const, id: 'LIST' },
        { type: 'BookingProspect' as const, id: bookingProspectId },
        { type: 'AppointmentsList' as const, id: 'LIST' },
        ...(result?.prospect.appointment_id
          ? [{ type: 'Appointment' as const, id: result.prospect.appointment_id }]
          : []),
      ],
    },
  },
});

// Patient change requests (reschedules awaiting staff decision, GDN-2272).
// Approving moves the appointment, so appointment caches refresh too.
const CHANGE_REQUEST_TAGS = [
  'ChangeRequestList',
  'Appointment',
  'AppointmentsList',
  'BookingProspectList',
] as const;

export const changeRequestsApi = changeRequestsGeneratedApi.enhanceEndpoints({
  addTagTypes: CHANGE_REQUEST_TAGS,
  endpoints: {
    getV2ProviderSchedulingChangeRequests: {
      providesTags: [{ type: 'ChangeRequestList' as const, id: 'LIST' }],
    },
    postV2ProviderSchedulingChangeRequestsByRequestIdApprove: {
      invalidatesTags: result => [
        { type: 'ChangeRequestList' as const, id: 'LIST' },
        { type: 'AppointmentsList' as const, id: 'LIST' },
        { type: 'BookingProspectList' as const, id: 'LIST' },
        ...(result?.request.appointment_id
          ? [{ type: 'Appointment' as const, id: result.request.appointment_id }]
          : []),
      ],
    },
    postV2ProviderSchedulingChangeRequestsByRequestIdDecline: {
      invalidatesTags: result => [
        { type: 'ChangeRequestList' as const, id: 'LIST' },
        { type: 'BookingProspectList' as const, id: 'LIST' },
        ...(result?.request.appointment_id
          ? [{ type: 'Appointment' as const, id: result.request.appointment_id }]
          : []),
      ],
    },
  },
});

// User-friendly hook names (never export the verbose generated names).
export const {
  useGetV2ProviderSchedulingGatesQuery: useSchedulingGatesQuery,
  usePostV2ProviderSchedulingGatesMutation: useCreateSchedulingGateMutation,
  usePatchV2ProviderSchedulingGatesByPolicyIdMutation: useUpdateSchedulingGateMutation,
  useDeleteV2ProviderSchedulingGatesByPolicyIdMutation: useDeleteSchedulingGateMutation,
} = schedulingGatesApi;

export const {
  useGetV2ProviderSchedulingConfirmationQuery: useConfirmationConfigQuery,
  usePutV2ProviderSchedulingConfirmationMutation: useSetConfirmationModeMutation,
  useDeleteV2ProviderSchedulingConfirmationByPolicyIdMutation:
    useDeleteConfirmationConfigMutation,
  usePostV2ProviderSchedulingBookingsByAppointmentIdConfirmMutation:
    useConfirmBookingMutation,
} = schedulingConfirmationApi;

export const {
  useGetV2ProviderSchedulingProspectsQuery: useBookingRequestsQuery,
  useGetV2ProviderSchedulingProspectsByBookingProspectIdQuery:
    useBookingRequestQuery,
  useGetV2ProviderSchedulingProspectsByBookingProspectIdMatchesQuery:
    useProspectMatchesQuery,
  usePostV2ProviderSchedulingProspectsByBookingProspectIdPromoteMutation:
    usePromoteProspectMutation,
  useDeleteV2ProviderSchedulingProspectsByBookingProspectIdMutation:
    useDeclineBookingRequestMutation,
} = bookingProspectsApi;

export const {
  useGetV2ProviderSchedulingChangeRequestsQuery: useChangeRequestsQuery,
  usePostV2ProviderSchedulingChangeRequestsByRequestIdApproveMutation:
    useApproveChangeRequestMutation,
  usePostV2ProviderSchedulingChangeRequestsByRequestIdDeclineMutation:
    useDeclineChangeRequestMutation,
} = changeRequestsApi;

export const {
  useGetV2ProviderAvailabilityRulesQuery: useAvailabilityRulesQuery,
  usePostV2ProviderAvailabilityRulesMutation: useCreateAvailabilityRuleMutation,
  usePatchV2ProviderAvailabilityRulesByAvailabilityRuleIdMutation: useUpdateAvailabilityRuleMutation,
  useDeleteV2ProviderAvailabilityRulesByAvailabilityRuleIdMutation: useDeleteAvailabilityRuleMutation,
} = availabilityRulesApi;
